import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import { fetchOrdersForApproval } from '../services/orderService';
import { finalizeOrderEvaluation } from '../services/evaluationService';
import OrderDetailsCard from './helper/OrderDetailsCard';
import OrderEditScore from './helper/OrderEditScore';

const EvaluationApproverPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [isEditing, setIsEditing] = useState({});
  const [orderCount, setOrderCount] = useState(0);

  const handleFetchOrders = async () => {
    console.log('[ApprovalPage] Fetching all auto + manual evaluated orders...');
    setLoading(true);
    try {
      const fetchedOrders = await fetchOrdersForApproval();
      setOrders(fetchedOrders);
      setOrderCount(fetchedOrders.length);
      console.log('[ApprovalPage] Orders fetched:', fetchedOrders);
    } catch (error) {
      console.error('[ApprovalPage] Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleEditScoreClick = (orderId) => {
    setIsEditing((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleSubmit = async (orderId) => {
    const userConfirmed = window.confirm('Are you sure you want to finalize this order?');
    if (userConfirmed) {
      console.log(`[EvaluationApproverPage] Finalizing order with ID: ${orderId}`);
      try {
        await finalizeOrderEvaluation(orderId);
        await handleFetchOrders();
        alert('Order finalized successfully');
      } catch (error) {
        console.error('[EvaluationApproverPage] Error finalizing order:', error);
        alert('Error finalizing order. Please try again.');
      }
    }
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString(); // Format as per local time
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-6 overflow-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">✅ Order Approval Dashboard</h1>
          <p className="text-lg text-gray-600">Total Orders Fetched: {orderCount}</p>
        </div>

        <p className="text-lg text-gray-600 mb-6">
          Orders whose <strong>order_status</strong> is <strong>"Under Review"</strong> and <strong>evaluation_status</strong> is <strong>"failed_in_auto_evaluation"</strong> or <strong>"manual_evaluation_done"</strong> will appear here.
        </p>

        <button
          onClick={handleFetchOrders}
          className="mb-6 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow"
        >
          Get All Auto + Manual Evaluated Orders
        </button>

        {loading && <p className="text-gray-600">Loading orders...</p>}

        {orders.length > 0 && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-700">Orders Ready for Approval</h2>

            <table className="min-w-full bg-white border rounded shadow">
              <thead className="bg-gray-200 text-gray-700">
                <tr>
                  <th className="px-4 py-2 border">Order ID</th>
                  <th className="px-4 py-2 border">Freelancer Name</th>
                  <th className="px-4 py-2 border">Plan</th>
                  <th className="px-4 py-2 border">Formatting</th>
                  <th className="px-4 py-2 border">Grammar</th>
                  <th className="px-4 py-2 border">SEO</th>
                  <th className="px-4 py-2 border">Manual</th>
                  <th className="px-4 py-2 border">Final</th>
                  <th className="px-4 py-2 border">Max Payout</th>
                  <th className="px-4 py-2 border">Eligible Payout</th>
                  <th className="px-4 py-2 border">Submitted At</th>
                  <th className="px-4 py-2 border">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <React.Fragment key={order.order_id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-2 border">{order.order_id}</td>
                      <td className="px-4 py-2 border">{order.freelancer_name || 'N/A'}</td>
                      <td className="px-4 py-2 border">{order.subscription_plan_name || 'N/A'}</td>
                      <td className="px-4 py-2 border">{order.cwe_a2_01_formatting_score}</td>
                      <td className="px-4 py-2 border">{order.cwe_a2_02_grammar_score}</td>
                      <td className="px-4 py-2 border">{order.cwe_a2_03_seo_score}</td>
                      <td className="px-4 py-2 border">{order.cwe_m_01_manual_score}</td>
                      <td className={`px-4 py-2 border ${order.cwe_final_score >= 65 ? 'font-bold text-green-500' : 'font-bold text-red-500'}`}>
                        {order.cwe_final_score}
                      </td>
                      <td className="px-4 py-2 border">
                        {order.max_payout ? `₹${order.max_payout}` : 'N/A'}
                      </td>
                      <td className="px-4 py-2 border">
                        {order.payout_eligible_amount ? `₹${order.payout_eligible_amount}` : 'N/A'}
                      </td>
                      <td className="px-4 py-2 border">
                        {order.submitted_at ? formatDateTime(order.submitted_at) : 'N/A'}
                      </td>
                      <td className="px-4 py-2 border">
                        <button
                          onClick={() => handleEditScoreClick(order.order_id)}
                          className="bg-green-500 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold"
                        >
                          {isEditing[order.order_id] ? 'Cancel Edit' : 'Edit Score'}
                        </button>

                        <button
                          onClick={() => toggleExpand(order.order_id)}
                          className="bg-blue-500 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold mx-2"
                        >
                          {expandedOrders[order.order_id] ? 'Collapse' : 'Expand'}
                        </button>

                        <button
                          onClick={() => handleSubmit(order.order_id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold shadow-md focus:outline-none"
                        >
                          Final Submit
                        </button>
                      </td>
                    </tr>

                    {expandedOrders[order.order_id] && (
                      <tr>
                        <td colSpan="12">
                          <OrderDetailsCard order={order} />
                        </td>
                      </tr>
                    )}

                    {isEditing[order.order_id] && (
                      <tr>
                        <td colSpan="12">
                          <OrderEditScore
                            order={order}
                            onCancelEdit={() => handleEditScoreClick(order.order_id)}
                            onSaveSuccess={() => handleEditScoreClick(order.order_id)}
                            handleFetchOrders={() => handleFetchOrders()}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluationApproverPage;
